"use strict";
/*---------------------------------------------------------------------------------------------
*  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
*--------------------------------------------------------------------------------------------*/
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DotnetCoreDependencyInstaller = void 0;
/* eslint-disable */ // When editing this file, please remove this and fix the linting concerns.
const cp = require("child_process");
const fs = require("fs");
const path = require("path");
const shelljs_1 = require("shelljs");
const vscode = require("vscode");
const moreInfoUrl = 'https://aka.ms/dotnet-linux-prereqs';
class DotnetCoreDependencyInstaller {
    constructor() {
        this.platform = process.platform;
    }
    signalIndicatesMissingLinuxDependencies(signal) {
        // If the dotnet does a SIGABRT on Linux this can mean that there are missing dependencies.
        // In fact, many signals can actually be an indicator of missing libraries on Linux as we have seen
        // SIGSEGV frequently as well. Checking the error stream is not accurate enough as on openSUSE15
        // we get a SIGABRT with no standard error output if libssl1_0_0 is missing (which it is vanilla).
        if (this.platform === 'linux' &&
            ['SIGABRT',
                'SIGIOT',
                'SIGSYS',
                'SIGPIPE',
                'SIGSEGV',
                'SIGILL',
                'SIGBUS',
                'SIGPFE',
                'SIGSYS'].includes(signal)) {
            return true;
        }
        return false;
    }
    installLinuxDependencies() {
        return __awaiter(this, arguments, void 0, function* (additionalLibs = {}, skipDotNetCore = false) {
            const scriptRoot = path.join(__dirname, '..', 'install scripts');
            const shellCommand = this.getShellCommand();
            // Determine the distro
            const result = cp.spawnSync(shellCommand, [path.join(scriptRoot, 'determine-linux-distro.sh')]);
            if (result.status !== 0) {
                console.log(`Failed to determine distro. Exit code: ${result.status}`);
                return result.status;
            }
            const distro = result.stdout.toString().trim();
            console.log(`Found distro ${distro}`);
            const additionalLibsKey = distro.toLowerCase(); // Always use lower case for this
            // Run the installer for the distro passing in any additional libs for it
            return this.executeCommandInTerminal('Linux dependency installer (.NET)', shellCommand, [path.join(scriptRoot, 'install-linux-prereqs.sh'),
                distro,
                (additionalLibs[additionalLibsKey] ? `"${additionalLibs[additionalLibsKey]}"` : ''),
                skipDotNetCore.toString(),
                moreInfoUrl]);
        });
    }
    promptLinuxDependencyInstall(message_1) {
        return __awaiter(this, arguments, void 0, function* (message, additionalLibs = {}, skipDotNetCore = false) {
            while (true) {
                const response = yield vscode.window.showErrorMessage(`${message} You may be missing key Linux libraries. Install them now?`, 'More Info', 'Install', 'Cancel');
                if (response === 'More Info') {
                    // Don't return, user has to explicitly hit "cancel"
                    vscode.commands.executeCommand('vscode.open', vscode.Uri.parse(moreInfoUrl));
                }
                else if (response === 'Install') {
                    const exitCode = yield this.installLinuxDependencies(additionalLibs, skipDotNetCore);
                    if (exitCode !== 0) {
                        const msg = (exitCode === 4 ?
                            'Your Linux distribution is not supported by the automated installer' :
                            'The dependency installer failed.');
                        // Terminal will pause for input on error so this is just an info message with a more info button
                        const failResponse = yield vscode.window.showErrorMessage(`${msg} Try installing dependencies manually.`, 'More Info');
                        if (failResponse === 'More Info') {
                            vscode.commands.executeCommand('vscode.open', vscode.Uri.parse(moreInfoUrl));
                        }
                        return false;
                    }
                    const reloadResponse = yield vscode.window.showInformationMessage('The dependency installer has completed successfully. Reload to activate your extensions!', 'Reload Now');
                    if (reloadResponse === 'Reload Now') {
                        yield vscode.commands.executeCommand('workbench.action.reloadWindow');
                    }
                    return true;
                }
                else {
                    // response === 'Cancel'
                    return false;
                }
            }
        });
    }
    executeCommandInTerminal(name_1, command_1) {
        return __awaiter(this, arguments, void 0, function* (name, command, args = [], promptAfterRun = false) {
            return new Promise((resolve, reject) => {
                var _a;
                const fullCommand = `"${command}" ${(((_a = args === null || args === void 0 ? void 0 : args.length) !== null && _a !== void 0 ? _a : 0) > 0 ? ` "${args.join('" "')}"` : '')}`;
                const exitCodeFile = path.join(__dirname, '..', `terminal-exit-code-${Math.floor(Math.random() * 1000000)}`);
                const commandList = new Array();
                if (this.platform === 'win32') {
                    // Note that "|| echo %ERRORLEVEL% >"" in this command sequence is a hack to get the exit code
                    // from the executed command given VS Code terminal does not return it.
                    commandList.push('cls', `echo 0 > "${exitCodeFile}"`, `${fullCommand} || echo %ERRORLEVEL% > "${exitCodeFile}"`);
                    if (promptAfterRun) {
                        commandList.push('pause');
                    }
                }
                else {
                    // Note that "|| echo $? >" in this command sequence is a hack to get the exit code from the
                    // executed command given VS Code terminal does not return it.
                    commandList.push('clear', `echo 0 > "${exitCodeFile}"`, `${fullCommand} || echo $? > "${exitCodeFile}"`);
                    if (promptAfterRun) {
                        commandList.push('echo "Press enter to close the terminal window."', 'sync', 'read');
                    }
                }
                commandList.push('exit 0');
                const commandSequence = commandList.join(' && ');
                // Use a long name to reduce the chances of overlap with other extension installers
                const terminal = vscode.window.createTerminal(name);
                terminal.sendText(commandSequence, true);
                terminal.show(false);
                // If the scripts executes successfully, reload
                vscode.window.onDidCloseTerminal((terminalEvent) => {
                    if (terminalEvent.name === terminal.name) {
                        // Hack to get exit code - VS Code terminal does not return it
                        try {
                            if (fs.existsSync(exitCodeFile)) {
                                const exitFile = fs.readFileSync(exitCodeFile).toString().trim();
                                fs.unlinkSync(exitCodeFile); // Delete file since we don't need it anymore
                                if (exitFile) {
                                    const exitCode = parseInt(exitFile, 10);
                                    if (exitCode === 0) {
                                        // Expected exit code
                                    }
                                    else {
                                        // Possible that this is an expected exit code, so just a warning
                                        console.log('Non-zero exit code detected.');
                                    }
                                    resolve(exitCode);
                                }
                                else {
                                    const message = `Terminal "${name}" run resulted in empty exit file. Command likely did not execute successfully.`;
                                    reject(new Error(message));
                                }
                            }
                            else {
                                const message = `Terminal "${name}" did not generate an exit file. Command likely did not execute successfully.`;
                                reject(new Error(message));
                            }
                        }
                        catch (err) {
                            reject(err);
                        }
                    }
                });
            });
        });
    }
    getShellCommand() {
        var _a, _b, _c, _d;
        if (this.platform === 'win32') {
            return (_b = (_a = (0, shelljs_1.which)('cmd')) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : 'cmd';
        }
        // Test for existence of bash which won't exist on the base Alpine Linux container, use sh instead there
        const shellCommand = (0, shelljs_1.which)('bash');
        // shellCommand will be null if bash is not found
        return shellCommand ? shellCommand.toString() : (_d = (_c = (0, shelljs_1.which)('sh')) === null || _c === void 0 ? void 0 : _c.toString()) !== null && _d !== void 0 ? _d : 'sh';
    }
}
exports.DotnetCoreDependencyInstaller = DotnetCoreDependencyInstaller;
//# sourceMappingURL=DotnetCoreDependencyInstaller.js.map